/* Pure helpers for the CRM Activity timeline (mockups/crm-activity-timeline.html,
   Variant A). No Firestore, no React, no DOM: everything here is a plain
   function over `interactions` records so it can be unit tested.

   Record shape: docs/client-interactions-spec.md. */

/* Filter chips, in display order. `key` is what filterRecords takes. */
export const ACTIVITY_CHIPS = [
  { key: "all", label: "All" },
  { key: "messages", label: "Messages" },
  { key: "quotes", label: "Quotes" },
  { key: "open", label: "Open" },
];

/* `at` is an ISO string in the spec, but a Firestore Timestamp can reach the
   client if a writer ever uses serverTimestamp() for it. Accept both. */
function isoOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.toDate === "function") {
    try { return value.toDate().toISOString(); } catch { return ""; }
  }
  if (value instanceof Date) return value.toISOString();
  return "";
}

/* Sort key: exact time when known, otherwise midnight on the record's date, so
   a date-only record and a timed record on the same day order deterministically.
   Records with neither field sort last. */
export function recordKey(r) {
  if (!r) return "";
  const at = isoOf(r.at);
  if (at) return at;
  const date = typeof r.date === "string" ? r.date : "";
  return date ? `${date}T00:00:00Z` : "";
}

/* Newest first. Ties break on doc id (descending) so a merged, deduped set from
   several listeners renders in a stable order. */
export function sortRecords(records) {
  return [...(records || [])].sort((a, b) => {
    const ka = recordKey(a);
    const kb = recordKey(b);
    if (ka !== kb) return ka < kb ? 1 : -1;
    return String(b?.id || "").localeCompare(String(a?.id || ""));
  });
}

export function isOpenItem(r) {
  return Boolean(r) && r.type === "open_item" && (r.status || "open") === "open";
}

/* "Open" means unresolved only: a done open_item drops out of that chip, the
   same as the mockup's CSS. Unknown chip keys fall back to everything. */
export function filterRecords(records, chip) {
  const all = records || [];
  if (chip === "messages") return all.filter(r => r && r.type === "message");
  if (chip === "quotes") return all.filter(r => r && r.type === "quote");
  if (chip === "open") return all.filter(isOpenItem);
  return all.filter(Boolean);
}

export function openCount(records) {
  return (records || []).filter(isOpenItem).length;
}

/* "MM-DD" inside the current year, "YYYY\nMM-DD" outside it (the timeline
   renders the column with white-space: pre-line). Anything unparseable comes
   back as the raw input so a bad record never blanks its row. */
export function fmtDate(date, now = new Date()) {
  const raw = isoOf(date) || (typeof date === "string" ? date : "");
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!m) return raw.trim();
  const [, year, month, day] = m;
  const currentYear = String(now instanceof Date ? now.getFullYear() : new Date().getFullYear());
  return year === currentYear ? `${month}-${day}` : `${year}\n${month}-${day}`;
}

export function gmailUrl(threadId) {
  const id = typeof threadId === "string" ? threadId.trim() : "";
  if (!id) return null;
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(id)}`;
}

/* "blake@nonverbalgroup.com" -> "Blake". Used for the who column on message
   rows; returns "" when there is nothing usable to show. */
export function senderLabel(r) {
  if (!r) return "";
  if (typeof r.who === "string" && r.who.trim()) return r.who.trim();
  const from = typeof r.from === "string" ? r.from.trim() : "";
  if (!from) return "";
  const angled = /<([^>]+)>/.exec(from);
  const addr = angled ? angled[1] : from;
  const local = addr.split("@")[0] || "";
  const first = local.split(/[._\-+]/)[0] || "";
  if (!first) return "";
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/* Small mono label to the left of the summary for non-message rows. */
export function kindLabel(r) {
  if (!r) return "";
  switch (r.type) {
    case "quote": return r.quoteEvent ? `Quote ${r.quoteEvent}` : "Quote";
    case "agreement": return "Agreement";
    case "preference": return "Preference";
    case "session": return "Session";
    case "note": return "Note";
    default: return "";
  }
}

/* Second line under the summary. Built from whatever the record actually has;
   returns "" when there is nothing to say, and the row then renders one line. */
export function detailLine(r) {
  if (!r) return "";
  const parts = [];
  if (r.type === "open_item") {
    if (r.owner) parts.push(`Owner: ${r.owner === "nyc" ? "NYC" : r.owner}`);
    if (r.due) parts.push(`Due ${r.due}`);
    if ((r.status || "open") === "done" && r.doneAt) parts.push(`Resolved ${r.doneAt}`);
  }
  const extra = [r.detail, r.type === "open_item" ? null : r.value]
    .find(v => typeof v === "string" && v.trim() && v.trim() !== (r.summary || "").trim());
  if (extra) parts.push(extra.trim());
  return parts.join(" · ");
}

/* Every stored message body is already stripped of quoted history and the
   signature by the writer, so the footer is unconditional on a message. It
   only grows a clause when the writer also had to cut the body for length. */
export function bodyFooter(r) {
  if (!r || r.type !== "message") return "";
  return r.bodyTruncated === true
    ? "Quoted history removed and cut for length"
    : "Quoted history removed";
}

/* Set by the writer when the message looked like it carried a credential or
   payment instruction: the summary is kept, the body is not stored. */
export function bodyOmittedNotice(r) {
  if (!r || r.type !== "message") return "";
  return r.bodyOmitted === "credential"
    ? "Body not stored (contained account or credential details)"
    : "";
}

function jobRevenue(job) {
  const n = Number(job?.grossRevenue);
  return Number.isFinite(n) ? n : 0;
}

function linksContact(job, contactId) {
  if (!job || !contactId) return false;
  if ((job.linkedContacts || []).some(l => l && l.contactId === contactId)) return true;
  // Legacy shape from before the contacts migration.
  return (job.linkedEntities || []).some(
    e => e && e.entityId === contactId && (!e.entityType || e.entityType === "contact"),
  );
}

/* Value of the projects a person is associated with, split so the two numbers
   never double count: a job that links the contact directly is only "direct",
   even when its client is also one of the contact's clients.

   Returns { jobCount, direct, viaCompany, directJobs, viaCompanyJobs }. */
export function associatedValue(contact, jobs) {
  const empty = { jobCount: 0, direct: 0, viaCompany: 0, directJobs: [], viaCompanyJobs: [] };
  if (!contact || !contact.id) return empty;

  const seen = new Set();
  const unique = (jobs || []).filter(j => {
    if (!j) return false;
    const key = j.id || j._docId || j.jobId;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const clientIds = new Set(
    [contact.clientId, ...(contact.linkedClients || []).map(l => l && l.clientId)].filter(Boolean),
  );

  const directJobs = [];
  const viaCompanyJobs = [];
  for (const job of unique) {
    if (linksContact(job, contact.id)) directJobs.push(job);
    else if (job.clientId && clientIds.has(job.clientId)) viaCompanyJobs.push(job);
  }

  return {
    jobCount: directJobs.length + viaCompanyJobs.length,
    direct: directJobs.reduce((s, j) => s + jobRevenue(j), 0),
    viaCompany: viaCompanyJobs.reduce((s, j) => s + jobRevenue(j), 0),
    directJobs,
    viaCompanyJobs,
  };
}
